import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  WithdrawalRedPacketConfigDTO,
  WithdrawalRedPacketConfigUpdateParams,
  WithdrawalRedPacketFormDTO,
  WithdrawalRedPacketJinshujuConfigDTO,
  WithdrawalRedPacketYaoyaolaAmountDTO,
  WithdrawalRedPacketYaoyaolaConfigDTO,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

export const RED_PACKET_PROVIDERS = [
  { value: 'yaoyaola_wechat_direct', label: '摇摇啦微信直发' },
  { value: 'jinshuju_form', label: '金数据表单' },
] as const;

type RedPacketProvider = (typeof RED_PACKET_PROVIDERS)[number]['value'];
type WithdrawalRedPacketForm = WithdrawalRedPacketConfigUpdateParams;

const DEFAULT_FORMS: WithdrawalRedPacketFormDTO[] = [
  { amount_fen: 1000, enabled: true, form_url: '' },
  { amount_fen: 2000, enabled: true, form_url: '' },
];

const EMPTY_JINSHUJU: WithdrawalRedPacketJinshujuConfigDTO = {
  enabled: false,
  callback_domain: '',
  ip_whitelist: [],
  forms: DEFAULT_FORMS,
};

const EMPTY_YAOYAOLA: WithdrawalRedPacketForm['yaoyaola'] = {
  enabled: false,
  uid: '',
  api_key: '',
  backend_base_url: 'https://www.yaoyaola.net',
  auth_domain_cache_seconds: 300,
  auth_callback_url: '',
  type: 0,
  title: '提现红包',
  sendname: '',
  wishing: '提现到账',
  min_amount_fen: 30,
  max_amount_fen: 20000,
  amounts: [],
};

const EMPTY_FORM: WithdrawalRedPacketForm = {
  enabled: false,
  provider_priority: ['yaoyaola_wechat_direct', 'jinshuju_form'],
  callback_domain: '',
  jinshuju: EMPTY_JINSHUJU,
  yaoyaola: EMPTY_YAOYAOLA,
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid withdrawal red packet config': '红包提现配置无效，请检查通道配置、金额和回调地址',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

function normalizeProviderPriority(values: string[] | undefined): RedPacketProvider[] {
  const allowed = RED_PACKET_PROVIDERS.map((item) => item.value);
  const seen = new Set<string>();
  const result: RedPacketProvider[] = [];

  for (const value of values ?? []) {
    if (allowed.includes(value as RedPacketProvider) && !seen.has(value)) {
      seen.add(value);
      result.push(value as RedPacketProvider);
    }
  }
  for (const value of allowed) {
    if (!seen.has(value)) {
      result.push(value);
    }
  }
  return result;
}

function normalizeForms(forms: WithdrawalRedPacketFormDTO[] | undefined): WithdrawalRedPacketFormDTO[] {
  if (!forms || forms.length === 0) {
    return DEFAULT_FORMS;
  }
  return forms
    .filter((item) => item.amount_fen > 0)
    .map((item) => ({
      amount_fen: item.amount_fen,
      enabled: item.enabled,
      form_url: item.form_url ?? '',
    }))
    .sort((a, b) => a.amount_fen - b.amount_fen);
}

function normalizeAmounts(amounts: WithdrawalRedPacketYaoyaolaAmountDTO[] | undefined): WithdrawalRedPacketYaoyaolaAmountDTO[] {
  return (amounts ?? [])
    .filter((item) => item.amount_fen > 0)
    .map((item, index) => ({
      amount_fen: item.amount_fen,
      label: item.label ?? '',
      enabled: item.enabled,
      sort_order: Number.isFinite(item.sort_order) ? item.sort_order : index + 1,
    }))
    .sort((a, b) => a.sort_order - b.sort_order || a.amount_fen - b.amount_fen);
}

function dtoToForm(dto: WithdrawalRedPacketConfigDTO): WithdrawalRedPacketForm {
  const callbackDomain = dto.callback_domain ?? dto.jinshuju?.callback_domain ?? EMPTY_JINSHUJU.callback_domain;
  const jinshuju = dto.jinshuju ?? {
    ...EMPTY_JINSHUJU,
    enabled: dto.enabled,
    callback_domain: callbackDomain,
    ip_whitelist: dto.ip_whitelist ?? [],
    forms: normalizeForms(dto.forms),
  };
  const yaoyaola = dto.yaoyaola ?? ({} as WithdrawalRedPacketYaoyaolaConfigDTO);

  return {
    enabled: dto.enabled,
    provider_priority: normalizeProviderPriority(dto.provider_priority),
    callback_domain: callbackDomain,
    jinshuju: {
      enabled: jinshuju.enabled,
      callback_domain: callbackDomain,
      ip_whitelist: jinshuju.ip_whitelist ?? [],
      forms: normalizeForms(jinshuju.forms),
    },
    yaoyaola: {
      enabled: yaoyaola.enabled ?? EMPTY_YAOYAOLA.enabled,
      uid: yaoyaola.uid ?? EMPTY_YAOYAOLA.uid,
      api_key: '',
      backend_base_url: yaoyaola.backend_base_url ?? EMPTY_YAOYAOLA.backend_base_url,
      auth_domain_cache_seconds: yaoyaola.auth_domain_cache_seconds ?? EMPTY_YAOYAOLA.auth_domain_cache_seconds,
      auth_callback_url: callbackDomain || yaoyaola.auth_callback_url || EMPTY_YAOYAOLA.auth_callback_url,
      type: yaoyaola.type ?? EMPTY_YAOYAOLA.type,
      title: yaoyaola.title ?? EMPTY_YAOYAOLA.title,
      sendname: yaoyaola.sendname ?? EMPTY_YAOYAOLA.sendname,
      wishing: yaoyaola.wishing ?? EMPTY_YAOYAOLA.wishing,
      min_amount_fen: yaoyaola.min_amount_fen ?? EMPTY_YAOYAOLA.min_amount_fen,
      max_amount_fen: yaoyaola.max_amount_fen ?? EMPTY_YAOYAOLA.max_amount_fen,
      amounts: normalizeAmounts(yaoyaola.amounts),
    },
  };
}

function listToText(values: string[]): string {
  return values.join('\n');
}

function parseListText(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[\n,，]+/)
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
}

function normalizeDomainText(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, '');
  if (!trimmed) {
    return '';
  }
  try {
    const parsed = new URL(trimmed);
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return trimmed;
  }
}

function buildPayload(
  form: WithdrawalRedPacketForm,
  jinshujuIPWhitelistText: string,
): WithdrawalRedPacketConfigUpdateParams {
  const apiKey = form.yaoyaola.api_key?.trim();
  const yaoyaola: WithdrawalRedPacketForm['yaoyaola'] = {
    ...form.yaoyaola,
    uid: form.yaoyaola.uid.trim(),
    backend_base_url: form.yaoyaola.backend_base_url.trim(),
    auth_callback_url: normalizeDomainText(form.callback_domain),
    title: form.yaoyaola.title.trim(),
    sendname: form.yaoyaola.sendname.trim(),
    wishing: form.yaoyaola.wishing.trim(),
    amounts: form.yaoyaola.amounts.map((item, index) => ({
      amount_fen: item.amount_fen,
      label: item.label.trim(),
      enabled: item.enabled,
      sort_order: item.sort_order || index + 1,
    })),
  };

  if (apiKey) {
    yaoyaola.api_key = apiKey;
  } else {
    delete yaoyaola.api_key;
  }

  return {
    enabled: form.enabled,
    provider_priority: normalizeProviderPriority(form.provider_priority),
    callback_domain: normalizeDomainText(form.callback_domain),
    jinshuju: {
      enabled: form.jinshuju.enabled,
      callback_domain: normalizeDomainText(form.callback_domain),
      ip_whitelist: parseListText(jinshujuIPWhitelistText),
      forms: form.jinshuju.forms.map((item) => ({
        amount_fen: item.amount_fen,
        enabled: item.enabled,
        form_url: item.form_url.trim(),
      })),
    },
    yaoyaola,
  };
}

export function useWithdrawalRedPacketConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<WithdrawalRedPacketConfigDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<WithdrawalRedPacketForm>(EMPTY_FORM);
  const [jinshujuIPWhitelistText, setJinshujuIPWhitelistText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getWithdrawalRedPacketConfig();
      const nextForm = dtoToForm(result);
      setData(result);
      setForm(nextForm);
      setJinshujuIPWhitelistText(listToText(nextForm.jinshuju.ip_whitelist));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateField = useCallback(<K extends keyof WithdrawalRedPacketForm>(key: K, value: WithdrawalRedPacketForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateJinshujuField = useCallback(
    <K extends keyof WithdrawalRedPacketJinshujuConfigDTO>(key: K, value: WithdrawalRedPacketJinshujuConfigDTO[K]) => {
      setForm((prev) => ({ ...prev, jinshuju: { ...prev.jinshuju, [key]: value } }));
    },
    [],
  );

  const updateYaoyaolaField = useCallback(
    <K extends keyof WithdrawalRedPacketForm['yaoyaola']>(key: K, value: WithdrawalRedPacketForm['yaoyaola'][K]) => {
      setForm((prev) => ({ ...prev, yaoyaola: { ...prev.yaoyaola, [key]: value } }));
    },
    [],
  );

  const updateJinshujuFormField = useCallback(
    <K extends keyof WithdrawalRedPacketFormDTO>(index: number, key: K, value: WithdrawalRedPacketFormDTO[K]) => {
      setForm((prev) => ({
        ...prev,
        jinshuju: {
          ...prev.jinshuju,
          forms: prev.jinshuju.forms.map((item, idx) => (idx === index ? { ...item, [key]: value } : item)),
        },
      }));
    },
    [],
  );

  const addJinshujuForm = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      jinshuju: {
        ...prev.jinshuju,
        forms: [...prev.jinshuju.forms, { amount_fen: 0, enabled: true, form_url: '' }],
      },
    }));
  }, []);

  const removeJinshujuForm = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      jinshuju: {
        ...prev.jinshuju,
        forms: prev.jinshuju.forms.filter((_, idx) => idx !== index),
      },
    }));
  }, []);

  const updateYaoyaolaAmountField = useCallback(
    <K extends keyof WithdrawalRedPacketYaoyaolaAmountDTO>(
      index: number,
      key: K,
      value: WithdrawalRedPacketYaoyaolaAmountDTO[K],
    ) => {
      setForm((prev) => ({
        ...prev,
        yaoyaola: {
          ...prev.yaoyaola,
          amounts: prev.yaoyaola.amounts.map((item, idx) => (idx === index ? { ...item, [key]: value } : item)),
        },
      }));
    },
    [],
  );

  const addYaoyaolaAmount = useCallback(() => {
    setForm((prev) => {
      const nextOrder = prev.yaoyaola.amounts.length + 1;
      return {
        ...prev,
        yaoyaola: {
          ...prev.yaoyaola,
          amounts: [
            ...prev.yaoyaola.amounts,
            { amount_fen: 0, label: '', enabled: true, sort_order: nextOrder },
          ],
        },
      };
    });
  }, []);

  const removeYaoyaolaAmount = useCallback((index: number) => {
    setForm((prev) => ({
      ...prev,
      yaoyaola: {
        ...prev.yaoyaola,
        amounts: prev.yaoyaola.amounts.filter((_, idx) => idx !== index),
      },
    }));
  }, []);

  const moveProvider = useCallback((index: number, direction: -1 | 1) => {
    setForm((prev) => {
      const next = [...normalizeProviderPriority(prev.provider_priority)];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) {
        return prev;
      }
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return { ...prev, provider_priority: next };
    });
  }, []);

  const validate = useCallback((payload: WithdrawalRedPacketConfigUpdateParams): boolean => {
    if (!payload.callback_domain.startsWith('http')) {
      addToast('error', '回调域名必须以 http 开头');
      return false;
    }
    if (payload.yaoyaola.enabled) {
      if (!payload.yaoyaola.uid) {
        addToast('error', '启用摇摇啦时必须填写 UID');
        return false;
      }
      if (!payload.yaoyaola.backend_base_url.startsWith('http')) {
        addToast('error', '摇摇啦后端接口域名必须以 http 开头');
        return false;
      }
      if (payload.yaoyaola.min_amount_fen <= 0 || payload.yaoyaola.max_amount_fen < payload.yaoyaola.min_amount_fen) {
        addToast('error', '摇摇啦单笔上下限配置无效');
        return false;
      }
      const typeMaxAmountFen = payload.yaoyaola.type === 1 ? 100000 : 20000;
      if (payload.yaoyaola.max_amount_fen > typeMaxAmountFen) {
        addToast('error', payload.yaoyaola.type === 1 ? '企业付款单笔上限不能超过 1000 元' : '普通红包单笔上限不能超过 200 元');
        return false;
      }
    }
    return true;
  }, [addToast]);

  const save = useCallback(async () => {
    const payload = buildPayload(form, jinshujuIPWhitelistText);
    if (!validate(payload)) {
      return false;
    }

    setSaving(true);
    try {
      const result = await settingsService.updateWithdrawalRedPacketConfig(payload);
      const nextForm = dtoToForm(result);
      setData(result);
      setForm(nextForm);
      setJinshujuIPWhitelistText(listToText(nextForm.jinshuju.ip_whitelist));
      addToast('success', '红包提现配置已保存');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, form, jinshujuIPWhitelistText, validate]);

  return {
    data,
    form,
    loading,
    saving,
    error,
    jinshujuIPWhitelistText,
    updateField,
    updateJinshujuField,
    updateYaoyaolaField,
    updateJinshujuFormField,
    addJinshujuForm,
    removeJinshujuForm,
    updateYaoyaolaAmountField,
    addYaoyaolaAmount,
    removeYaoyaolaAmount,
    moveProvider,
    setJinshujuIPWhitelistText,
    save,
    reload: load,
  };
}
